import { makeStyles } from '@fluentui/react-components';
import { tokens } from '@bingads-webui-fluent-component/tokens';

/**
 * Fluent V2 overrides for PIDL payment form controls (@cspayments/pidlsdk).
 * PIDL renders its own DOM with pidlddc-* class prefix; not iframed for BingAds.
 * Extracted from get-form-field-overrides.js to stay under the max-lines lint limit.
 */
export const usePidlFormOverrides = makeStyles({
  pidlOverrides: {
    // Labels
    '& .pidlddc-label': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      paddingBottom: tokens.spacingVerticalXS,
    },
    // Input fields
    '& .pidlddc-input-property': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRadius: tokens.borderRadiusMedium,
      height: tokens.spacingVerticalXXXL,
      paddingLeft: tokens.spacingHorizontalMNudge,
      paddingRight: tokens.spacingHorizontalMNudge,
      paddingTop: '0',
      paddingBottom: '0',
      outlineStyle: 'none',
      boxSizing: 'border-box',
      transitionProperty: 'none',
      boxShadow: 'none',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .pidlddc-input-property:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .pidlddc-input-property:focus': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: `${tokens.strokeWidthThick} !important`,
      outline: 'none !important',
    },
    '& .pidlddc-input-property::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
    // Dropdown fields
    '& .pidlddc-input-dropdown': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRadius: tokens.borderRadiusMedium,
      height: tokens.spacingVerticalXXXL,
      paddingLeft: tokens.spacingHorizontalMNudge,
      paddingRight: tokens.spacingHorizontalXXXL,
      paddingTop: '0',
      paddingBottom: '0',
      outlineStyle: 'none',
      boxSizing: 'border-box',
      transitionProperty: 'none',
      boxShadow: 'none',
      appearance: 'none',
      // eslint-disable-next-line quotes
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2.22 4.47a.75.75 0 0 1 1.06 0L6 7.19l2.72-2.72a.75.75 0 0 1 1.06 1.06L6.53 8.78a.75.75 0 0 1-1.06 0L2.22 5.53a.75.75 0 0 1 0-1.06z' fill='%23424242'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: `right ${tokens.spacingHorizontalMNudge} center`,
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .pidlddc-input-dropdown:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .pidlddc-input-dropdown:focus': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: `${tokens.strokeWidthThick} !important`,
      outline: 'none !important',
    },
    // Submit button (PrimaryButton appearance)
    '& #pidlddc-button-submitButton': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      backgroundColor: tokens.colorBrandBackground,
      borderTopColor: tokens.colorBrandBackground,
      borderRightColor: tokens.colorBrandBackground,
      borderBottomColor: tokens.colorBrandBackground,
      borderLeftColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      cursor: 'pointer',
      outlineStyle: 'none',
      minWidth: 'auto',
      height: tokens.spacingVerticalXXXL,
    },
    '& #pidlddc-button-submitButton:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      borderTopColor: tokens.colorBrandBackgroundHover,
      borderRightColor: tokens.colorBrandBackgroundHover,
      borderBottomColor: tokens.colorBrandBackgroundHover,
      borderLeftColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& #pidlddc-button-submitButton:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      borderTopColor: tokens.colorBrandBackgroundPressed,
      borderRightColor: tokens.colorBrandBackgroundPressed,
      borderBottomColor: tokens.colorBrandBackgroundPressed,
      borderLeftColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& #pidlddc-button-submitButton:disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: tokens.colorNeutralStrokeDisabled,
      borderRightColor: tokens.colorNeutralStrokeDisabled,
      borderBottomColor: tokens.colorNeutralStrokeDisabled,
      borderLeftColor: tokens.colorNeutralStrokeDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
    // Cancel button (DefaultButton appearance)
    '& #pidlddc-button-cancelButton': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: tokens.colorNeutralStroke1,
      borderRightColor: tokens.colorNeutralStroke1,
      borderBottomColor: tokens.colorNeutralStroke1,
      borderLeftColor: tokens.colorNeutralStroke1,
      backgroundColor: tokens.colorNeutralBackground1,
      color: tokens.colorNeutralForeground1,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      cursor: 'pointer',
      outlineStyle: 'none',
      minWidth: 'auto',
      height: tokens.spacingVerticalXXXL,
    },
    '& #pidlddc-button-cancelButton:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
      color: tokens.colorNeutralForeground1Hover,
    },
    '& #pidlddc-button-cancelButton:active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      borderTopColor: tokens.colorNeutralStroke1Pressed,
      borderRightColor: tokens.colorNeutralStroke1Pressed,
      borderBottomColor: tokens.colorNeutralStroke1Pressed,
      borderLeftColor: tokens.colorNeutralStroke1Pressed,
      color: tokens.colorNeutralForeground1Pressed,
    },
    '& #pidlddc-button-cancelButton:disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: tokens.colorNeutralStrokeDisabled,
      borderRightColor: tokens.colorNeutralStrokeDisabled,
      borderBottomColor: tokens.colorNeutralStrokeDisabled,
      borderLeftColor: tokens.colorNeutralStrokeDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
    // Validation error text
    '& .pidlddc-error': {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorPaletteRedForeground1,
      lineHeight: tokens.lineHeightBase200,
    },
    // Validation error border
    '& .pidlddc-errorstroke': {
      borderBottomColor: `${tokens.colorPaletteRedBorder2} !important`,
    },
  },
});
